import "../styles/search.css";
import type { ComponentChildren, JSX } from "preact";
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import type { CardKind, Space } from "../../../shared/types.ts";
import { ENTITY_WORD, GRAMMAR_VERSION } from "../search/index.ts";
import { IconClose, IconImage, IconLink, IconNote, IconSearch } from "./Icons.tsx";
import { SpaceChips } from "./spaces/SpaceChips.tsx";
import { chips, facetToken, go, kindCounts, meaning, query, route, searchFor, searchResult, store, text, topTags, width } from "./state.ts";

/** Foca a busca; fora de Tudo (Lixeira, Coleções…), volta para Tudo primeiro. */
export function focusSearch() {
  const el = document.getElementById("search");
  if (el) return el.focus();
  go("/");
  requestAnimationFrame(() => document.getElementById("search")?.focus());
}

const PLURAL: Record<CardKind, string> = {
  note: "Notas", image: "Imagens", link: "Links", quote: "Citações", highlight: "Destaques",
  color: "Cores", pdf: "PDFs", video: "Vídeos", audio: "Áudios", file: "Arquivos",
};
const ENTITY_PLURAL: Record<string, string> = { Book: "Livros", Recipe: "Receitas", Product: "Produtos", Article: "Artigos", Movie: "Filmes" };
const LANG_NAME: Record<string, string> = {
  pt: "Português", en: "Inglês", es: "Espanhol", fr: "Francês", de: "Alemão", it: "Italiano", nl: "Holandês", ru: "Russo",
  ja: "Japonês", zh: "Chinês", ko: "Coreano", ar: "Árabe",
};
// Ícones próprios das facetas novas (traço simples, viewBox 24, como Icons.tsx).
const glyph = (d: string) => () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
const IconEntity = glyph("M7 3.5h10a1 1 0 0 1 1 1v16l-6-4-6 4v-16a1 1 0 0 1 1-1z");
const IconLang = glyph("M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18zM3.5 12h17M12 3c2.5 2.6 3.6 5.6 3.6 9s-1.1 6.4-3.6 9c-2.5-2.6-3.6-5.6-3.6-9s1.1-6.4 3.6-9z");
const KIND_ICON: Partial<Record<CardKind, () => JSX.Element>> = { note: IconNote, image: IconImage, link: IconLink };
// Cor do anel do chip de etiqueta: decorativa, estável pelo nome.
const RINGS = ["var(--accent)", "var(--success)", "var(--tone-3)"];
const ringOf = (name: string) => RINGS[[...name].reduce((h, ch) => h + ch.charCodeAt(0), 0) % RINGS.length];
const cards = (n: number) => (n === 1 ? "1 card" : `${n} cards`);
const byCount = (o: Record<string, number>) => Object.entries(o).sort((a, b) => b[1] - a[1]);

type Facet = { token: string; label: string; n: number; icon?: () => JSX.Element; swatch?: string; kind: string };

/** Chips da linha de facetas: tipos, assuntos (entidades), idiomas e cores do conjunto pesquisado. */
function facetList(): Facet[] {
  const r = searchResult.value;
  if (!r) return [];
  return [
    ...kindCounts.value.map(([k, n]) => ({ token: facetToken(k), label: PLURAL[k], n, icon: KIND_ICON[k] ?? IconNote, kind: "tipo" })),
    ...byCount(r.entities).map(([e, n]) => ({ token: `tipo:${ENTITY_WORD[e]}`, label: ENTITY_PLURAL[e] ?? e, n, icon: IconEntity, kind: "assunto" })),
    ...byCount(r.langs).map(([l, n]) => ({ token: `idioma:${l}`, label: LANG_NAME[l] ?? l.toUpperCase(), n, icon: IconLang, kind: "idioma" })),
    ...r.colors.map((c) => ({ token: `cor:${c.name}`, label: c.name[0].toUpperCase() + c.name.slice(1), n: c.count, swatch: c.hex, kind: "cor" })),
  ];
}

/** Aviso discreto da busca: semântica a caminho, sem rede, ou tipo que não existe (não casa nada). */
function searchNote(): string {
  if (meaning.value === "loading") return "Buscando por significado…";
  if (meaning.value === "offline") return "Sem conexão: a busca por significado volta com a rede.";
  const unknown = query.value ? searchResult.value?.query.unknownTypes[0] : undefined;
  return unknown ? `Não existe o tipo “${unknown}”. Tente tipo:imagem, tipo:nota, tipo:link ou tipo:livro.` : "";
}

/** Chips que rolam de lado (celular): marca a ponta com chip escondido, que esmaece (CSS) em vez de cortar seco. */
function edges(ul: HTMLElement) {
  ul.toggleAttribute("data-l", ul.scrollLeft > 1);
  ul.toggleAttribute("data-r", ul.scrollLeft + ul.clientWidth < ul.scrollWidth - 1);
}

/**
 * Coleção automática salva por um app mais novo (Space.queryVersion > GRAMMAR_VERSION, lacuna #37): esta versão pode
 * ler a consulta de outro jeito, então avisa em vez de fingir que o resultado está certo.
 */
export function QueryVersionNote({ space }: { space: Space }) {
  if (space.kind !== "smart" || (space.queryVersion ?? 0) <= GRAMMAR_VERSION) return null;
  return (
    <p class="space-stale" role="status">
      Esta coleção foi salva por uma versão mais nova do fai.g. Atualize o app (recarregue a página) para ver os cards certos.
    </p>
  );
}

/**
 * Linha de busca serifada (desktop) ou barra-card (≤ 1024). Enter fixa o termo como chip; com foco, a linha de baixo
 * troca as etiquetas mais usadas pelas facetas (tipos, assuntos, idiomas e cores, com contagem). `lead` entra antes do
 * campo (menu no celular), `children` depois (o "+" do desktop), antes da dica de atalhos.
 */
export function Search({ lead, children }: { lead?: ComponentChildren; children?: ComponentChildren }) {
  const [open, setOpen] = useState(false);
  // No celular os chips rolam de lado dentro da barra: o que acabou de entrar fica à vista, colado no campo.
  const chipList = useRef<HTMLUListElement>(null);
  const nChips = chips.value.length;
  useLayoutEffect(() => {
    const ul = chipList.current;
    if (!ul) return;
    ul.scrollTo({ left: ul.scrollWidth });
    edges(ul);
  }, [nChips]);
  const toggle = (token: string) =>
    (chips.value = chips.value.includes(token) ? chips.value.filter((c) => c !== token) : [...chips.value, token]);
  const onKeyDown = (e: KeyboardEvent) => {
    const input = e.currentTarget as HTMLInputElement;
    const term = text.value.trim();
    if (e.key === "Enter" && term) {
      e.preventDefault();
      if (!chips.value.includes(term)) chips.value = [...chips.value, term];
      text.value = "";
    } else if (e.key === "Backspace" && !input.value && chips.value.length) {
      chips.value = chips.value.slice(0, -1);
    } else if (e.key === "Escape") {
      e.preventDefault(); // não deixa o navegador limpar o campo; Esc só sai da busca
      input.blur();
    }
  };
  const tags = topTags.value.slice(0, 8);
  const facets = open ? facetList() : [];
  const note = searchNote();
  // A linha existe sempre que não se digita (mesmo vazia): focar a busca troca o conteúdo sem empurrar a grade.
  // Digitando, ela só aparece para o aviso (semântica a caminho, sem rede, tipo desconhecido).
  const row = text.value
    ? note
      ? "note"
      : null
    : facets.length
      ? "facets"
      : note
        ? "note"
        : !query.value && (tags.length || store.spaces.value.length)
          ? "tags"
          : "empty";
  return (
    <div
      class="search"
      role="search"
      data-open={open || undefined}
      // Abre só quando o campo ganha foco (Menu e "+" não abrem); fica aberta enquanto o foco anda dentro da busca.
      onFocusIn={(e) => (e.target as Element).id === "search" && setOpen(true)}
      onFocusOut={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setOpen(false)}
    >
      <div class="search-line">
        <div class="search-box" onClick={(e) => e.target === e.currentTarget && focusSearch()}>
          {lead}
          <span class="search-icon" aria-hidden="true">
            <IconSearch />
          </span>
          {chips.value.length > 0 && (
            <ul class="chips" ref={chipList} aria-label="Filtros da busca" onScroll={(e) => edges(e.currentTarget)}>
              {chips.value.map((c) => (
                <li class="chip" key={c}>
                  {c}
                  <button type="button" aria-label={`Tirar o filtro ${c}`} onClick={() => toggle(c)}>
                    <IconClose />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <input
            id="search"
            type="search"
            autocomplete="off"
            autocapitalize="none"
            autocorrect="off"
            spellcheck={false} // o iOS trocaria "relogio" por "relógio" e mexeria em site: e tipo:
            enterkeyhint="search"
            aria-label="Buscar"
            placeholder={width.value > 768 ? "Procure no seu acervo…" : "Procurar"}
            value={text.value}
            onInput={(e) => {
              text.value = e.currentTarget.value;
              if (route.value.path !== "/") go("/");
            }}
            onKeyDown={onKeyDown}
          />
          {children}
          {/* Dica de atalho no fim da linha (hover/foco): "?" abre a lista de atalhos. */}
          <span class="keyhint" aria-hidden="true">
            <b>?</b> Atalhos
          </span>
        </div>
      </div>
      {row === "empty" && <div class="chip-row empty-row" aria-hidden="true" />}
      {row === "note" && (
        <p class={`search-note${meaning.value === "loading" ? " is-busy" : ""}`} aria-hidden="true">
          {note}
        </p>
      )}
      {/* Sempre no DOM: o leitor de tela ouve a mudança mesmo quando a linha mostra as facetas. */}
      <p class="sr-only" role="status">
        {note}
      </p>
      {row === "facets" && (
        <div class="chip-row facets" role="group" aria-label="Refinar a busca">
          {facets.map((f) => {
            const Icon = f.icon;
            return (
              <button
                type="button"
                class="facet"
                key={f.token}
                data-kind={f.kind}
                aria-label={`${f.kind === "tipo" ? "" : `${f.kind[0].toUpperCase()}${f.kind.slice(1)}: `}${f.label}, ${cards(f.n)}`}
                aria-pressed={chips.value.includes(f.token)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => toggle(f.token)}
              >
                {Icon ? <Icon /> : <span class="facet-swatch" style={`--sw:${f.swatch}`} aria-hidden="true" />}
                {f.label}
                <span class="facet-n" aria-hidden="true">
                  {f.n}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {row === "tags" && (
        <div class="chip-row tags" role="group" aria-label="Coleções e etiquetas mais usadas">
          <SpaceChips />
          {tags.map((t) => (
            <button type="button" class="tag-chip" key={t.id} style={`--ring:${ringOf(t.name)}`} aria-label={`Buscar a etiqueta ${t.name}`} onClick={() => searchFor(`#${t.name}`)}>
              {t.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
